import type { EnvironmentSummary, EnvName, EnvRole, Operation, SiteDetail, SiteKind } from "../../api/types";
import { isActiveOp } from "../../api/types";
import { IconCode, IconFlask, IconLayers, IconSites } from "../../ui/icons";

/**
 * The name of an environment's Database & files and SSH parts, as its site has them: a static site or a Node.js app
 * has no database, so they're Files and SSH & SFTP there. The rail, the breadcrumb and the page title use this.
 */
export function envPartLabel(part: "data" | "access", kind: SiteKind | undefined): string {
  const noDb = kind === "static" || kind === "node";
  if (part === "data") return noDb ? "Files" : "Database & files";
  // The database's login has its own part now (Database; env/DatabasePart.tsx).
  return "SSH & SFTP";
}

/** Left to right, the way code moves. */
export const PIPELINE: EnvName[] = ["dev", "test", "live"];

type Named = { name: EnvRole; label?: string; slug?: string };

/**
 * What to call an environment: "Dev", "Test", "Live", or an extra environment's own name ("customers").
 * Takes a pipeline name, or the environment itself (whose `label` the API sends).
 */
export function envLabel(x: EnvName | string | Named): string {
  // A slug: Dev, Test and Live by their names, an extra environment by its own.
  if (typeof x === "string") return x === "live" ? "Live" : x === "test" ? "Test" : x === "dev" ? "Dev" : x;
  if (x.label) return x.label;
  if (x.name === "extra") return x.slug ?? "extra";
  return envLabel(x.name);
}

/** "Dev's", "customers'": the possessive of an environment's name. */
export const poss = (label: string) => (/s$/.test(label) ? `${label}'` : `${label}'s`);

/** The address segment for an environment: its slug ("dev", "customers"). */
export const envSlug = (e: Named) => (e.name === "extra" ? (e.slug ?? "extra") : e.name);

export const isExtra = (e: { name: EnvRole; kind?: string }) => e.name === "extra" || e.kind === "extra";

/** A small mark for each environment, the same wherever it's named: code, the lab, the world, and a stack for the extra ones. */
export const ENV_ICON = { dev: IconCode, test: IconFlask, live: IconSites, extra: IconLayers } as const;

export const sha7 = (sha: string | null | undefined) => (sha ? sha.slice(0, 7) : "");

/** git's "Maya Chen <maya@example.com>" → "Maya Chen". */
export function authorName(author: string | null | undefined): string | null {
  if (!author) return null;
  const name = author.replace(/\s*<[^>]*>\s*$/, "").trim();
  return name || author.trim() || null;
}

/** Older deploys can be rolled back to while their release is still on the server. */
export const canRollBackTo = (d: { current: boolean; available: boolean; status: string }) => d.available && !d.current && d.status === "succeeded";

/** Dev, Test and Live by name. Extra environments aren't in it (see extrasOf). */
export function envsByName(site: SiteDetail): Partial<Record<EnvName, EnvironmentSummary>> {
  const out: Partial<Record<EnvName, EnvironmentSummary>> = {};
  for (const e of site.environments) if (!isExtra(e)) out[e.name as EnvName] = e;
  if (!out.live && site.live) out.live = site.live;
  return out;
}

/** The site's extra environments the caller can see, by name. */
export function extrasOf(site: Pick<SiteDetail, "environments">): EnvironmentSummary[] {
  return site.environments.filter(isExtra).sort((a, b) => envSlug(a).localeCompare(envSlug(b)));
}

/** The environments that exist, in pipeline order. */
export function chain(envs: Partial<Record<EnvName, EnvironmentSummary>>): EnvironmentSummary[] {
  return PIPELINE.map((n) => envs[n]).filter((e): e is EnvironmentSummary => !!e);
}

/**
 * Where an environment's code comes from: the environment to its left in the
 * chain (Live takes Test's commit, or Dev's when there's no Test). Null for
 * the first one, which deploys a branch.
 */
export function deploySource(name: EnvRole, envs: Partial<Record<EnvName, EnvironmentSummary>>): EnvironmentSummary | null {
  // An extra environment deploys its own branch; nothing promotes into it.
  if (name === "extra") return null;
  const c = chain(envs);
  const i = c.findIndex((e) => e.name === name);
  return i > 0 ? c[i - 1]! : null;
}

/** The operation currently running against an environment, if any. */
export function activeOpFor(envId: string, ops: Operation[] | undefined, kinds?: string[]): Operation | undefined {
  return (ops ?? []).find((o) => o.environmentId === envId && isActiveOp(o.status) && (!kinds || kinds.includes(o.kind)));
}

/** A password that's easy to read aloud to a client and hard to guess. */
export function suggestPassword(): string {
  const alphabet = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  let out = "";
  for (let i = 0; i < bytes.length; i++) {
    out += alphabet[bytes[i]! % alphabet.length];
    if (i === 3 || i === 7 || i === 11) out += "-";
  }
  return out;
}
